import { c as _c } from "react/compiler-runtime";
import ReactECharts from 'echarts-for-react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { EChartsOption } from 'echarts';
import { useTranslation } from '@/hooks/use-translation';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { ChartEmptyFrame } from '../figure/ChartEmptyFrame';
import { ChartFigure } from '../figure/ChartFigure';
import { ChartKeyboardAxis } from '../figure/ChartKeyboardAxis';
import { ChartLegend, type ChartLegendItem } from '../figure/ChartLegend';
import type { ChartLegendMarkKind } from '../figure/ChartLegendMark';
import { useChartFigure } from '../hooks/use-chart-figure';
import { usePrintableChart } from '../hooks/use-printable-chart';
import { useScreenPlotHeight } from '../hooks/use-screen-plot-height';
import { SVG_RENDERER } from '../renderer';
import { serieValueAt } from '../interfaces';
import { frictionZoneAt, useFrictionZonesSeries, type FrictionZoneLabels } from './hooks/use-friction-zones-series';
import { FRICTION_GRID, touchWeightPlotHeight, WEIGHT_SCALE, useTouchWeightOption } from './hooks/use-touch-weight-option';
import { TOUCH_WEIGHT_SERIE_IDS, useTouchWeightSeries, type TouchWeightSerieId, type TouchWeightSeries } from './hooks/use-touch-weight-series';

/**
 * What the chart hands to the reading strip at its head.
 *
 * `ChartReadingContext`, which the other figures use, carries one measured
 * series and its reference curves; this figure draws four series of equal
 * standing, so it describes its own reading instead.
 */
export interface TouchWeightReadingContext {
  /** The zone the friction of that key falls in, already localised. */
  frictionZoneLabel: string | null;
  hoveredKey: number | null;
  series: TouchWeightSeries;
}

/** The glyph and colour each series carries in the legend. */
const LEGEND_MARKS: Record<TouchWeightSerieId, {
  colorClassName: string;
  mark: ChartLegendMarkKind;
}> = {
  balance: {
    colorClassName: 'text-ptl-series-balance',
    mark: 'diamond'
  },
  down: {
    colorClassName: 'text-ptl-series-down',
    mark: 'triangle-down'
  },
  friction: {
    colorClassName: 'text-ptl-series-friction',
    mark: 'dot'
  },
  up: {
    colorClassName: 'text-ptl-series-up',
    mark: 'triangle-up'
  }
};
const ALL_VISIBLE: Record<TouchWeightSerieId, boolean> = {
  balance: true,
  down: true,
  friction: true,
  up: true
};
export interface TouchWeightChartProps {
  analyzedKeyboard: TouchWeightAnalyzedKeyboard;
  /** Said over the empty figure, until there is something to draw. */
  emptyState?: React.ReactNode;
  /**
   * The reading strip at the head of the figure. It is a function so that the
   * strip reads the very series the chart draws, rather than computing them
   * again.
   */
  renderReading?: (context: TouchWeightReadingContext) => React.ReactNode;
  useSupportSpringMeasurements: boolean;
  variant?: 'analyze' | 'design';
}
export const TouchWeightChart = t0 => {
  const $ = _c(76);
  const {
    analyzedKeyboard,
    emptyState,
    renderReading,
    useSupportSpringMeasurements,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    geometry,
    hover,
    keys
  } = useChartFigure(analyzedKeyboard, 2);
  const option = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = touchWeightPlotHeight();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const screenPlotHeight = useScreenPlotHeight(t2, variant);
  const {
    onChartReady,
    plotHeight,
    tokens
  } = usePrintableChart(variant, screenPlotHeight, option, hover.bindChart);
  const extraWeightHeight = Math.max(0, plotHeight - touchWeightPlotHeight());
  const series = useTouchWeightSeries(analyzedKeyboard, useSupportSpringMeasurements);
  const [visibleSeries, setVisibleSeries] = useState(ALL_VISIBLE);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id => {
      setVisibleSeries(current => ({
        ...current,
        [id]: !current[id]
      }));
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const toggleSerie = t3;
  let t4;
  if ($[2] !== t) {
    t4 = t("High");
    $[2] = t;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t) {
    t5 = t("Low");
    $[4] = t;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = t("Medium");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      high: t4,
      low: t5,
      medium: t6
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const zoneLabels = t7;
  let t8;
  if ($[12] !== geometry || $[13] !== keys || $[14] !== tokens || $[15] !== zoneLabels) {
    t8 = {
      axisIndex: FRICTION_GRID,
      geometry,
      keys,
      labels: zoneLabels,
      tokens
    };
    $[12] = geometry;
    $[13] = keys;
    $[14] = tokens;
    $[15] = zoneLabels;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const frictionZones = useFrictionZonesSeries(t8);
  let t9;
  if ($[17] !== t) {
    t9 = t("FRICTION (g)");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("WEIGHT (g)");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = {
      friction: t9,
      weight: t10
    };
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const panelTitles = t11;
  let t12;
  if ($[24] !== currentLanguage || $[25] !== extraWeightHeight || $[26] !== frictionZones || $[27] !== geometry || $[28] !== panelTitles || $[29] !== series || $[30] !== tokens || $[31] !== visibleSeries) {
    t12 = {
      extraWeightHeight,
      frictionZones,
      geometry,
      locale: currentLanguage,
      panelTitles,
      series,
      tokens,
      visibleSeries
    };
    $[24] = currentLanguage;
    $[25] = extraWeightHeight;
    $[26] = frictionZones;
    $[27] = geometry;
    $[28] = panelTitles;
    $[29] = series;
    $[30] = tokens;
    $[31] = visibleSeries;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const nextOption = useTouchWeightOption(t12);
  let t13;
  let t14;
  if ($[33] !== nextOption) {
    t13 = () => {
      option.current = nextOption;
    };
    t14 = [nextOption];
    $[33] = nextOption;
    $[34] = t13;
    $[35] = t14;
  } else {
    t13 = $[34];
    t14 = $[35];
  }
  useLayoutEffect(t13, t14);
  let t15;
  if ($[36] !== series || $[37] !== visibleSeries) {
    t15 = TOUCH_WEIGHT_SERIE_IDS.map(id_0 => ({
      colorClassName: LEGEND_MARKS[id_0].colorClassName,
      id: id_0,
      isVisible: visibleSeries[id_0],
      label: series[id_0].name,
      mark: LEGEND_MARKS[id_0].mark,
      onToggle: () => toggleSerie(id_0)
    }));
    $[36] = series;
    $[37] = visibleSeries;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const legendItems = t15;
  const {
    hoveredKey
  } = hover;
  let t16;
  bb0: {
    if (hoveredKey === null) {
      t16 = null;
      break bb0;
    }
    const zone = frictionZoneAt(serieValueAt(series.friction, hoveredKey), hoveredKey, analyzedKeyboard.size);
    t16 = zone ? zoneLabels[zone] : null;
  }
  const frictionZoneLabel = t16;
  const isEmpty = series.balance.data.every(_temp);
  if (isEmpty && emptyState) {
    let t17;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = touchWeightPlotHeight();
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== emptyState || $[41] !== geometry || $[42] !== keys || $[43] !== variant) {
      t18 = <ChartEmptyFrame geometry={geometry} height={t17} keys={keys} scale={WEIGHT_SCALE} variant={variant}>{emptyState}</ChartEmptyFrame>;
      $[40] = emptyState;
      $[41] = geometry;
      $[42] = keys;
      $[43] = variant;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    return t18;
  }
  let t17;
  if ($[45] !== frictionZoneLabel || $[46] !== hoveredKey || $[47] !== renderReading || $[48] !== series) {
    t17 = renderReading?.({
      frictionZoneLabel,
      hoveredKey,
      series
    });
    $[45] = frictionZoneLabel;
    $[46] = hoveredKey;
    $[47] = renderReading;
    $[48] = series;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== legendItems) {
    t18 = <ChartLegend items={legendItems} />;
    $[50] = legendItems;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const t19 = hover.setHoveredKey;
  let t20;
  if ($[52] !== t) {
    t20 = t("Keyboard: hover a key to read its measurement");
    $[52] = t;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== hover.setHoveredKey || $[55] !== hoveredKey || $[56] !== keys || $[57] !== t20) {
    t21 = <ChartKeyboardAxis keys={keys} highlightedKey={hoveredKey} onKeyHover={t19} description={t20} />;
    $[54] = hover.setHoveredKey;
    $[55] = hoveredKey;
    $[56] = keys;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const t22 = hover.figureProps;
  let t23;
  if ($[59] !== extraWeightHeight) {
    t23 = touchWeightPlotHeight(extraWeightHeight);
    $[59] = extraWeightHeight;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t23) {
    t24 = {
      height: t23,
      width: "100%"
    };
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== nextOption || $[64] !== onChartReady || $[65] !== t24) {
    t25 = <ReactECharts option={nextOption} notMerge={true} onChartReady={onChartReady} opts={SVG_RENDERER} style={t24} />;
    $[63] = nextOption;
    $[64] = onChartReady;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== geometry || $[68] !== hover.figureProps || $[69] !== hoveredKey || $[70] !== t17 || $[71] !== t18 || $[72] !== t21 || $[73] !== t25 || $[74] !== variant) {
    t26 = <ChartFigure reading={t17} variant={variant} geometry={geometry} highlightedKey={hoveredKey} header={t18} keyboard={t21} {...t22}>{t25}</ChartFigure>;
    $[67] = geometry;
    $[68] = hover.figureProps;
    $[69] = hoveredKey;
    $[70] = t17;
    $[71] = t18;
    $[72] = t21;
    $[73] = t25;
    $[74] = variant;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  const figure = t26;
  return figure;
};
function _temp(point) {
  return point.payload === undefined;
}